import './tiptap-editor.css';

import {
  LinkIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  TextBIcon,
  TextItalicIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from '@tiptap/markdown';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Button, Stack } from 'src/components';
import { color } from 'src/constants/color.css';

import { Color } from '../../../constants';

const LinkMark = Link.extend({ inclusive: false }).configure({
  openOnClick: false,
});

type Props = {
  value: string;
  onChange: (markdown: string) => void;
  onPasteFiles?: (files: File[]) => boolean;
  placeholder?: string;
};

export default function TiptapEditor({
  value,
  onChange,
  onPasteFiles,
  placeholder = 'Your comment',
}: Props) {
  const [linkPanelOpen, setLinkPanelOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const [linkPanelTop, setLinkPanelTop] = React.useState(0);
  const linkInputRef = React.useRef<HTMLInputElement>(null);
  const linkPanelRef = React.useRef<HTMLDivElement>(null);
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  const editorAreaRef = React.useRef<HTMLDivElement>(null);

  const openLinkPanelBelow = (bottom: number, url: string) => {
    const area = editorAreaRef.current;
    if (area) setLinkPanelTop(bottom - area.getBoundingClientRect().top + 4);
    setLinkUrl(url);
    setLinkPanelOpen(true);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      LinkMark,
      Placeholder.configure({ placeholder, showOnlyCurrent: false }),
      Markdown,
    ],
    editorProps: {
      handlePaste: (_view, event) => {
        const clipboard = event.clipboardData;
        const files = Array.from(clipboard?.files ?? []);
        const hasText = clipboard?.types.includes('text/plain');
        if (!onPasteFiles || files.length === 0 || hasText) return false;
        return onPasteFiles(files);
      },
    },
    content: value,
    // Empty markdown hides the placeholder until the first transaction.
    ...(value ? { contentType: 'markdown' as const } : {}),
    onUpdate: ({ editor: instance }) => {
      onChange(instance.getMarkdown());
    },
  });

  const activeMarks = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance?.isActive('bold') ?? false,
      italic: instance?.isActive('italic') ?? false,
      bulletList: instance?.isActive('bulletList') ?? false,
      orderedList: instance?.isActive('orderedList') ?? false,
      link: instance?.isActive('link') ?? false,
    }),
  });

  React.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (value === editor.getMarkdown()) return;
    editor.commands.setContent(value, {
      contentType: 'markdown',
      emitUpdate: false,
    });
  }, [value, editor]);

  React.useEffect(() => {
    if (linkPanelOpen) linkInputRef.current?.focus();
  }, [linkPanelOpen]);

  React.useEffect(() => {
    if (!linkPanelOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        linkPanelRef.current?.contains(target) ||
        toolbarRef.current?.contains(target)
      ) {
        return;
      }
      setLinkPanelOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [linkPanelOpen]);

  // Not editor.view.dom: the view can already be destroyed by the time an
  // effect runs.
  const onEditorClick = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) return;
    e.preventDefault();
    openLinkPanelBelow(
      anchor.getBoundingClientRect().bottom,
      anchor.getAttribute('href') ?? ''
    );
  };

  if (!editor || !activeMarks) return null;

  const toggleLinkPanel = () => {
    if (linkPanelOpen) {
      setLinkPanelOpen(false);
      return;
    }
    setLinkPanelTop(0);
    setLinkUrl(editor.getAttributes('link').href ?? '');
    setLinkPanelOpen(true);
  };

  const saveLink = () => {
    const url = linkUrl.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    if (url) {
      chain.setLink({ href: url }).run();
    } else {
      chain.unsetLink().run();
    }
    setLinkPanelOpen(false);
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkPanelOpen(false);
  };

  return (
    <Stack gap={0.5}>
      <html.div ref={toolbarRef} style={styles.toolbar}>
        <ToolbarButton
          active={activeMarks.bold}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <TextBIcon size={20} color={Color.neutral80} />
        </ToolbarButton>
        <ToolbarButton
          active={activeMarks.italic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <TextItalicIcon size={20} color={Color.neutral80} />
        </ToolbarButton>
        <ToolbarButton
          active={activeMarks.bulletList}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListBulletsIcon size={20} color={Color.neutral80} />
        </ToolbarButton>
        <ToolbarButton
          active={activeMarks.orderedList}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListNumbersIcon size={20} color={Color.neutral80} />
        </ToolbarButton>
        <ToolbarButton
          active={activeMarks.link || linkPanelOpen}
          onClick={toggleLinkPanel}
        >
          <LinkIcon size={20} color={Color.neutral80} />
        </ToolbarButton>
      </html.div>
      <html.div ref={editorAreaRef} style={styles.editorArea}>
        {linkPanelOpen && (
          <html.div
            ref={linkPanelRef}
            style={[styles.linkPanel, styles.linkPanelPos(linkPanelTop)]}
          >
            <html.div
              style={styles.closeButton}
              onClick={() => setLinkPanelOpen(false)}
            >
              <XIcon size={20} color={Color.neutral60} />
            </html.div>
            <html.input
              ref={linkInputRef}
              style={styles.linkInput}
              value={linkUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLinkUrl(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveLink();
                }
              }}
              placeholder="Enter URL"
            />
            {activeMarks.link && (
              <Button
                label="Remove Link"
                variant="text"
                size="small"
                iconLeft={(p) => <TrashIcon size={p.size} color={p.color} />}
                onClick={removeLink}
              />
            )}
            <Button label="Save" size="small" onClick={saveLink} />
          </html.div>
        )}
        <div {...css.props(styles.editorWrap)} onClick={onEditorClick}>
          <EditorContent editor={editor} />
        </div>
      </html.div>
    </Stack>
  );
}

function ToolbarButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div
      {...css.props(styles.toolbarButton, active && styles.toolbarButtonActive)}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

const styles = css.create({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  editorArea: {
    position: 'relative',
  },
  linkPanel: {
    position: 'absolute',
    left: 10,
    width: '95%',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    backgroundColor: color.neutral0,
    boxShadow: '0px 4px 22px rgba(0, 0, 0, 0.1)',
  },
  linkPanelPos: (top: number) => ({ top }),
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    ':hover': {
      opacity: 0.9,
    },
  },
  linkInput: {
    flexGrow: 1,
    minWidth: 0,
    height: 40,
    paddingInline: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: color.neutral0,
    outline: 'none',
    '::placeholder': {
      color: color.neutral40,
    },
    ':focus': {
      borderColor: color.neutral60,
    },
  },
  toolbarButton: {
    display: 'flex',
    alignItems: 'center',
    padding: 10,
    borderRadius: 8,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
  toolbarButtonActive: {
    backgroundColor: color.neutral10,
  },
  editorWrap: {
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    padding: 12,
    minHeight: 150,
  },
});
